/**
 * iter 72 (Phase E) — Platform Developer "Org Unification" tab.
 *
 * Mounted inside the Dev Console as its own tab. Surfaces:
 *   - A list of all migration jobs, filterable by status.
 *   - Drill-down detail with full preflight + user decisions + role/dept mappings.
 *   - Annotate-before-approving field (appended to preflight_report.staff_notes[]).
 *   - APPROVE & EXECUTE  /  REJECT  /  EMERGENCY ABORT (with revert) buttons.
 *   - Live progress monitor with batch ETA + retry chip.
 *   - "Download migration receipt" button (Phase F) for completed jobs.
 *
 * This is the kill-switch tier — the only path that can actually start the
 * engine. Org A's Super Admin can only "submit for review"; THIS UI is what
 * approves it.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise, CheckCircle, X, WarningCircle, FileText, Note,
  Stop, Play, Hourglass,
} from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";

const STATUS_FILTERS = [
  { v: "",                label: "All"               },
  { v: "pending_review",  label: "Pending review"    },
  { v: "approved",        label: "Approved (queued)" },
  { v: "in_progress",     label: "In progress"       },
  { v: "finalizing",      label: "Finalizing"        },
  { v: "paused",          label: "Paused"            },
  { v: "completed",       label: "Completed"         },
  { v: "failed",          label: "Failed"            },
  { v: "aborted",         label: "Aborted"           },
];

const STATUS_COLOR = {
  pending_review: "bg-amber-100 text-amber-700",
  approved:       "bg-blue-100 text-blue-700",
  in_progress:    "bg-blue-100 text-blue-700",
  finalizing:     "bg-blue-100 text-blue-700",
  paused:         "bg-amber-100 text-amber-700",
  completed:      "bg-emerald-100 text-emerald-700",
  failed:         "bg-rose-100 text-rose-700",
  aborted:        "bg-rose-100 text-rose-700",
};

export default function DevUnificationTab() {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("pending_review");
  const [open, setOpen] = useState(null);   // job_id of opened detail

  const load = async () => {
    try {
      const r = await api.get(filter ? `/unify/admin/jobs?status=${filter}` : "/unify/admin/jobs");
      setJobs(r.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [filter]);

  // Poll every 4s when any non-terminal job is in the list.
  useEffect(() => {
    const anyLive = jobs.some(j =>
      ["approved", "pre_flight", "countdown", "in_progress", "finalizing"].includes(j.status));
    if (!anyLive) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [jobs, filter]);

  return (
    <div className="space-y-4" data-testid="dev-unify-tab">
      <div className="surface p-4 flex items-center gap-3 flex-wrap" data-testid="dev-unify-filters">
        <span className="label-xs">FILTER</span>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.v}
            className={`btn btn-sm ${filter === f.v ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setFilter(f.v)}
            data-testid={`dev-unify-filter-${f.v || "all"}`}
          >{f.label}</button>
        ))}
        <button
          className="btn btn-sm btn-ghost ml-auto"
          onClick={load}
          data-testid="dev-unify-refresh-btn"
          title="Refresh"
        ><ArrowsClockwise size={12} /></button>
      </div>

      {jobs.length === 0 ? (
        <div className="surface p-12 text-center text-muted-foreground" data-testid="dev-unify-empty">
          <Hourglass size={32} weight="thin" className="mx-auto mb-3" />
          No migration jobs match this filter.
        </div>
      ) : (
        <div className="surface overflow-hidden">
          <table className="dense w-full" data-testid="dev-unify-jobs-table">
            <thead><tr>
              <th>STATUS</th><th>MODE</th>
              <th>SOURCE → TARGET</th>
              <th>SUBMITTED</th>
              <th>PROGRESS</th>
              <th>ACTIONS</th>
            </tr></thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} data-testid={`dev-unify-row-${j.id}`}>
                  <td>
                    <span className={`text-[10px] uppercase mono px-2 py-0.5 rounded ${STATUS_COLOR[j.status] || "bg-muted/40"}`}>
                      {j.status}
                    </span>
                  </td>
                  <td className="uppercase mono text-xs">{j.mode}</td>
                  <td>
                    <div className="font-semibold">{j.source_name}</div>
                    <div className="text-xs text-muted-foreground">→ {j.target_name}</div>
                  </td>
                  <td className="text-xs text-muted-foreground">
                    {j.submitted_at ? new Date(j.submitted_at).toLocaleString() : "—"}
                  </td>
                  <td className="text-xs">
                    {j.total_batches
                      ? `${j.batches_completed}/${j.total_batches}${j.current_table ? ` · ${j.current_table}` : ""}`
                      : "—"}
                    {j.retry_count > 0 && (
                      <span className="ml-2 text-amber-700 mono" title="Batch retries">↻{j.retry_count}</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary btn-xs"
                      onClick={() => setOpen(j.id)}
                      data-testid={`dev-unify-open-${j.id}`}
                    >REVIEW</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <JobDetailDrawer
          jobId={open}
          onClose={() => setOpen(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}


/* ========================================================================= */
/* Detail drawer                                                              */
/* ========================================================================= */
function JobDetailDrawer({ jobId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [rejReason, setRejReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/unify/admin/jobs/${jobId}/full`);
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); onClose(); }
  };
  useEffect(() => { load(); }, [jobId]);

  // Poll while in non-terminal state.
  useEffect(() => {
    if (!data) return;
    const s = data.job.status;
    if (["completed", "failed", "aborted"].includes(s)) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [data?.job?.status]);

  const submitNote = async () => {
    if (!notes.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/unify/admin/jobs/${jobId}/notes`, { notes: notes.trim() });
      setNotes("");
      await load();
      toast.success("Note saved.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doApprove = async () => {
    if (!confirm("APPROVE this migration and EXECUTE NOW?\n\nThis kicks off the engine. Org B's users will be locked out within ~5 minutes.")) return;
    setBusy(true);
    try {
      await api.post(`/unify/admin/jobs/${jobId}/approve`);
      toast.success("Approved — engine started.");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (!rejReason.trim()) { toast.error("Reject reason required."); return; }
    if (!confirm(`Reject this migration?\n\nReason will be visible to the submitter.`)) return;
    setBusy(true);
    try {
      await api.post(`/unify/admin/jobs/${jobId}/reject`, { reason: rejReason.trim() });
      toast.success("Rejected.");
      onChanged?.(); onClose();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const doAbort = async () => {
    if (!confirm(
      "EMERGENCY ABORT this migration?\n\n" +
      "If the engine has already moved rows, they will be REVERTED via the " +
      "original_org_id breadcrumb. Source org will be UN-sealed and users " +
      "will regain access. \n\n" +
      "WARNING: Stripe Connect tokens and OAuth client secrets that were " +
      "cleared cannot be restored from the database — affected integrations " +
      "must be re-linked manually."
    )) return;
    setBusy(true);
    try {
      const r = await api.post(`/unify/admin/jobs/${jobId}/abort`);
      const total = Object.values(r.data?.reverted_rows_by_table || {}).reduce((a, b) => a + b, 0);
      toast.success(`Aborted. ${total} rows reverted to source org.`);
      await load(); onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const downloadReceipt = () => {
    // Direct browser fetch with auth header → trigger save-as.  We use axios
    // to authenticate, then create a Blob.
    api.get(`/unify/jobs/${jobId}/receipt`, { responseType: "blob" }).then(r => {
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `migration-receipt-${jobId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(e => toast.error(formatErr(e)));
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50">
        <div className="surface p-6 text-sm">Loading…</div>
      </div>
    );
  }

  const job = data.job;
  const pf = job.preflight_report || {};
  const status = job.status;
  const pct = job.total_batches ? Math.min(100, Math.round((job.batches_completed / job.total_batches) * 100)) : 0;
  const isPending = status === "pending_review";
  const isLive = ["approved", "pre_flight", "countdown", "in_progress", "finalizing", "paused"].includes(status);
  const isTerminal = ["completed", "failed", "aborted"].includes(status);
  const isComplete = status === "completed";

  return (
    <div className="fixed inset-0 z-[120] flex" data-testid="dev-unify-detail-overlay">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-[840px] max-w-[95vw] h-full bg-background border-l border-border overflow-y-auto">
        <div className="sticky top-0 bg-background/95 backdrop-blur border-b border-border p-4 z-10 flex items-center gap-3">
          <div className="flex-1">
            <div className="label-xs">JOB {jobId.slice(0, 8)}…</div>
            <div className="text-lg font-semibold">{job.source_name} → {job.target_name}</div>
            <div className="text-xs text-muted-foreground">
              Mode <strong className="uppercase">{job.mode}</strong> · Status{" "}
              <span className={`mono px-2 py-0.5 rounded text-[10px] uppercase ${STATUS_COLOR[status] || "bg-muted/40"}`}>{status}</span>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>CLOSE</button>
        </div>

        <div className="p-5 space-y-5">

          {/* Live progress */}
          {(isLive || isTerminal) && (
            <section className="surface p-4" data-testid="dev-unify-progress">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="label-xs">PROGRESS</span>
                <span className="mono">
                  {job.batches_completed}/{job.total_batches || "—"} batches
                  {pct > 0 ? ` · ${pct}%` : ""}
                  {job.retry_count > 0 && <span className="ml-2 text-amber-700">↻{job.retry_count}</span>}
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                <div className="h-2 bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
              </div>
              {job.current_table && (
                <div className="text-xs text-muted-foreground mt-2">
                  Currently: <code className="mono">{job.current_table}</code>
                </div>
              )}
              {job.last_error && (
                <div className="surface p-3 mt-2 border-destructive/40 text-xs">
                  <div className="label-xs text-destructive mb-1">LAST ERROR</div>
                  <pre className="whitespace-pre-wrap font-mono">{job.last_error}</pre>
                </div>
              )}
            </section>
          )}

          {/* Pre-flight blockers */}
          {(pf.blockers || []).length > 0 && (
            <section className="surface p-4 border-destructive/40" data-testid="dev-unify-blockers">
              <div className="label-xs text-destructive mb-2 flex items-center gap-2">
                <WarningCircle size={14} weight="duotone" /> BLOCKERS AT SUBMISSION
              </div>
              <ul className="text-sm space-y-1">
                {pf.blockers.map((b, i) => (
                  <li key={i} className="flex items-start gap-2"><X size={14} className="text-destructive mt-1 shrink-0" /><span>{b.message}</span></li>
                ))}
              </ul>
            </section>
          )}

          {/* Totals */}
          <section className="surface p-4">
            <div className="label-xs mb-2">ROW TOTALS (SNAPSHOT)</div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 text-xs">
              {Object.entries(pf.totals || {}).map(([k, v]) => (
                <div key={k} className="surface p-2 bg-muted/30">
                  <div className="text-muted-foreground">{k}</div>
                  <div className="text-lg mono">{v}</div>
                </div>
              ))}
            </div>
          </section>

          {/* User decisions */}
          <section className="surface p-4" data-testid="dev-unify-decisions">
            <div className="label-xs mb-2">USER DECISIONS ({data.user_decisions.length})</div>
            {data.user_decisions.length === 0 ? (
              <div className="text-xs text-muted-foreground">No collisions — all source users port as-is.</div>
            ) : (
              <table className="dense">
                <thead><tr><th>SOURCE</th><th>ACTION</th><th>TARGET / SUFFIX</th></tr></thead>
                <tbody>
                  {data.user_decisions.map(d => (
                    <tr key={d.id}>
                      <td className="mono text-xs">{d.source_user_id.slice(0, 8)}…</td>
                      <td className="uppercase mono text-xs">{d.action}</td>
                      <td>{d.target_user_id || d.suffix_email || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Staff notes */}
          <section className="surface p-4" data-testid="dev-unify-staff-notes">
            <div className="label-xs mb-2 flex items-center gap-2"><Note size={14} /> STAFF NOTES (AUDIT TRAIL)</div>
            <div className="space-y-2 mb-3">
              {(pf.staff_notes || []).length === 0 ? (
                <div className="text-xs text-muted-foreground">No notes yet.</div>
              ) : (
                pf.staff_notes.map((n, i) => (
                  <div key={i} className="surface p-2 bg-muted/30 text-xs" data-testid={`dev-unify-note-${i}`}>
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span><strong>{n.by}</strong></span>
                      <span className="mono">{new Date(n.at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))
              )}
            </div>
            <textarea
              className="input min-h-[60px]"
              placeholder="Verified with submitter on phone 2026-06-29 · everything checks out."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              data-testid="dev-unify-note-input"
            />
            <button
              className="btn btn-secondary btn-sm mt-2"
              onClick={submitNote}
              disabled={busy || !notes.trim()}
              data-testid="dev-unify-note-add-btn"
            >ADD NOTE</button>
          </section>

          {/* Action buttons */}
          <section className="surface p-4" data-testid="dev-unify-actions">
            <div className="label-xs mb-2">PLATFORM ACTIONS</div>
            <div className="flex gap-2 flex-wrap">
              {isPending && (
                <>
                  <button className="btn btn-primary" onClick={doApprove} disabled={busy} data-testid="dev-unify-approve-btn">
                    <Play size={14} /> APPROVE & EXECUTE
                  </button>
                  <div className="flex-1 min-w-[260px]">
                    <input
                      className="input"
                      placeholder="Reject reason (required)"
                      value={rejReason}
                      onChange={e => setRejReason(e.target.value)}
                      data-testid="dev-unify-reject-reason"
                    />
                  </div>
                  <button className="btn btn-destructive" onClick={doReject} disabled={busy || !rejReason.trim()} data-testid="dev-unify-reject-btn">
                    <X size={14} /> REJECT
                  </button>
                </>
              )}
              {isLive && !isComplete && (
                <button className="btn btn-destructive" onClick={doAbort} disabled={busy} data-testid="dev-unify-abort-btn">
                  <Stop size={14} /> EMERGENCY ABORT + REVERT
                </button>
              )}
              {isComplete && (
                <button className="btn btn-primary" onClick={downloadReceipt} data-testid="dev-unify-receipt-btn">
                  <FileText size={14} /> DOWNLOAD MIGRATION RECEIPT
                </button>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
